import { z } from 'zod'
import {
  trainerChannelAttachmentSchema,
  trainerChannelClientInviteSchema,
} from './trainer-channel-read'
import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'

/**
 * Channel attachment and invite handlers keep their existing auth, idempotency,
 * and validation order. These passthrough request contracts only describe the
 * accepted transport for clients and independent conformance fixtures.
 */
export const trainerChannelAttachmentArchiveRequestSchema = z.object({}).passthrough()
export const trainerChannelClientInviteCreateRequestSchema = z.object({
  expires_in_hours: z.unknown().optional(),
  expiresInHours: z.unknown().optional(),
}).passthrough()
export const trainerChannelClientInviteRevokeRequestSchema = z.object({}).passthrough()

const attachmentLifecycleSchema = z.object({
  operation: z.enum(['archive', 'restore']),
  channelLoaded: z.literal(true),
  attachmentLoaded: z.literal(true),
  db1RowRetained: z.literal(true),
  providerDeleteCalled: z.literal(false),
  r2Touched: z.literal(false),
  providerHeadersReturned: z.literal(false),
  objectKeyReturned: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const attachmentWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  providerHeadersReturned: z.literal(false),
  objectKeyReturned: z.literal(false),
  queuePublished: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const inviteWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.boolean(),
  db2Touched: z.literal(false),
}).passthrough()

export const trainerChannelAttachmentArchiveResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  status: z.enum(['archived', 'restored']),
  mode: z.literal('db1_soft_archive'),
  attachment: trainerChannelAttachmentSchema,
  idempotentReplay: z.boolean(),
  attachmentLifecycle: attachmentLifecycleSchema,
  sideEffects: attachmentWriteSideEffectsSchema,
}).passthrough()

export const trainerChannelClientInviteWriteResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  invite: trainerChannelClientInviteSchema,
  idempotentReplay: z.boolean(),
  sideEffects: inviteWriteSideEffectsSchema,
}).passthrough()

export type TrainerChannelAttachmentArchiveRequest = z.infer<typeof trainerChannelAttachmentArchiveRequestSchema>
export type TrainerChannelClientInviteCreateRequest = z.infer<typeof trainerChannelClientInviteCreateRequestSchema>
export type TrainerChannelClientInviteRevokeRequest = z.infer<typeof trainerChannelClientInviteRevokeRequestSchema>
export type TrainerChannelAttachmentArchiveResponse = z.infer<typeof trainerChannelAttachmentArchiveResponseSchema>
export type TrainerChannelClientInviteWriteResponse = z.infer<typeof trainerChannelClientInviteWriteResponseSchema>
